/**
 * Audit tool — pb audit <BET-ID> as an MCP tool.
 *
 * BET-182 Slice 1: MCP tool wrapper around the auditBet Convex query.
 * Thin client — all gate logic lives in convex/agentKnowledge/audit.ts.
 *
 * Chain: BET-182, STD-113, STD-65 (structured output)
 */

import { z } from "zod/v3";
import { kernelQuery } from "../client.js";
import { success, failureResult } from "../envelope.js";
import { replaceVocabTokens, type WorkspaceVocabCtx } from "../lib/canonicalRefs.js";
import { getRequestApiKey } from "../auth.js";

// ─── Vocab cache (WP-438 Slice 2) ────────────────────────────────────────────
// Mirrors the 5-min TTL pattern from packages/cli/src/lib/workspaceVocabCache.ts.
// Keyed by API key (stdio: env-derived single key; http: per-request bearer)
// so multi-tenant MCP servers don't cross-contaminate vocab between workspaces.
// Fail-open: if a fetch raises we cache nothing and the next call retries.
// Bounded with MAX_VOCAB_KEYS + LRU/oldest-eviction to keep long-lived HTTP
// MCP servers from leaking Map entries (mirrors auth.ts keyStateMap pattern).
const VOCAB_TTL_MS = 5 * 60 * 1000;
const MAX_VOCAB_KEYS = 100;
interface VocabCacheEntry {
  ctx: WorkspaceVocabCtx | undefined;
  expiresAt: number;
}
const vocabCache = new Map<string, VocabCacheEntry>();
function getCachedVocab(key: string): WorkspaceVocabCtx | undefined | "miss" {
  const entry = vocabCache.get(key);
  if (!entry) return "miss";
  if (Date.now() >= entry.expiresAt) {
    vocabCache.delete(key);
    return "miss";
  }
  return entry.ctx;
}
function setCachedVocab(key: string, ctx: WorkspaceVocabCtx | undefined): void {
  vocabCache.set(key, { ctx, expiresAt: Date.now() + VOCAB_TTL_MS });
  evictVocabIfFull();
}
function evictVocabIfFull(): void {
  if (vocabCache.size <= MAX_VOCAB_KEYS) return;
  // Drop expired entries first (sweep).
  const now = Date.now();
  for (const [k, v] of vocabCache) {
    if (now >= v.expiresAt) vocabCache.delete(k);
  }
  // Still over? Drop oldest by expiresAt (proxy for insertion order under fixed TTL).
  if (vocabCache.size > MAX_VOCAB_KEYS) {
    const sorted = [...vocabCache.entries()].sort((a, b) => a[1].expiresAt - b[1].expiresAt);
    const excess = vocabCache.size - MAX_VOCAB_KEYS;
    for (let i = 0; i < excess; i++) vocabCache.delete(sorted[i][0]);
  }
}

// ─── Schema ─────────────────────────────────────────────────────────────────

const AUDIT_ACTIONS = ["run"] as const;

export const auditSchema = z.object({
  action: z.enum(AUDIT_ACTIONS).describe(
    "'run': run the hygiene audit for a bet entry.",
  ),
  entryId: z.string().describe("Bet entry ID to audit, e.g. '<PREFIX>-<n>'"),
  phase: z.enum(["shaping", "handoff"]).default("shaping").optional()
    .describe(
      "'shaping': check shaping-phase fields only. 'handoff': check all required fields including buildContract/buildSequence/exclusions/risks. Default: shaping.",
    ),
});

// ─── Output types (mirrors AuditOutput from convex/agentKnowledge/audit.ts) ───

interface GateResult {
  gate: string;
  status: "pass" | "fail" | "warn";
  blocking: boolean;
  detail: string;
  fix?: string;
  fixType?: "exact" | "template" | "manual";
  hint?: string;
  /** WP-438 S1: per-field flags indicating unresolved {{vocab:...}} tokens. */
  tokens?: {
    detail: boolean;
    fix: boolean;
    hint: boolean;
  };
}

interface AuditResult {
  entryId: string;
  entryName: string;
  phase: string;
  clean: boolean;
  verdict: "pass" | "fail" | "warn";
  gates: GateResult[];
  summary: {
    total: number;
    passed: number;
    failed: number;
    warned: number;
  };
  elapsed_ms: number;
}

// WP-484 S3 (clean break, TEN-2157): the standalone `audit` tool retired
// here. handleAuditRun stays live — quality.ts (the `quality` compound,
// action=audit) is its only remaining caller.

// ─── Handler ─────────────────────────────────────────────────────────────────

export async function handleAuditRun(entryId: string, phase: "shaping" | "handoff") {
  let result: AuditResult;

  try {
    result = await kernelQuery<AuditResult>("chain.auditBet", { entryId, phase });
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    if (msg.includes("not found") || msg.includes("NOT_FOUND")) {
      return failureResult(
        `Entry '${entryId}' not found.`,
        "NOT_FOUND",
        `Entry '${entryId}' not found.`,
        "Use entries action=search to find the correct BET-ID.",
        [{ tool: "entries", description: "Search entries", parameters: { action: "search", query: entryId } }],
      );
    }
    throw err;
  }

  // ── WP-438 S1: resolve {{vocab:...}} tokens before rendering. ──────────────
  // Fetch workspace vocab context (PAT-230, DEC-1021). Fail-open: if the
  // vocab fetch fails the resolver still works — it just falls back to the
  // canonicalKey literal per `getCollectionLabel` Layer 3.
  //
  // WP-438 Slice 2: per-API-key 5-min cache so successive audit calls in the
  // same MCP session skip the chain.getVocabulary round-trip. Mirrors the CLI
  // pattern in packages/cli/src/lib/workspaceVocabCache.ts.
  let vocabCtx: WorkspaceVocabCtx | undefined;
  const cacheKey = getRequestApiKey() ?? "__stdio_default__";
  const cached = getCachedVocab(cacheKey);
  if (cached !== "miss") {
    vocabCtx = cached;
  } else {
    try {
      type VocabResult = { collectionLabels?: Record<string, unknown>; collectionDefaults?: Record<string, unknown> };
      const vocab = await kernelQuery<VocabResult>("chain.getVocabulary", {});
      if (vocab?.collectionLabels || vocab?.collectionDefaults) {
        vocabCtx = {
          ...(vocab.collectionLabels ? { collectionLabels: vocab.collectionLabels as WorkspaceVocabCtx['collectionLabels'] } : {}),
          ...(vocab.collectionDefaults ? { collectionDefaults: vocab.collectionDefaults as WorkspaceVocabCtx['collectionDefaults'] } : {}),
        };
      }
      setCachedVocab(cacheKey, vocabCtx);
    } catch { /* fail-open — vocab labels are advisory; don't cache failures */ }
  }

  // Resolve every gate field flagged with tokens.* === true. The Convex
  // producer only flags strings that contain `{{vocab:`, so this is a cheap
  // per-field pass — fields without tokens are skipped.
  for (const gate of result.gates) {
    if (gate.tokens?.detail) gate.detail = replaceVocabTokens(gate.detail, vocabCtx);
    if (gate.tokens?.fix && gate.fix) gate.fix = replaceVocabTokens(gate.fix, vocabCtx);
    if (gate.tokens?.hint && gate.hint) gate.hint = replaceVocabTokens(gate.hint, vocabCtx);
  }

  const { verdict, gates, summary, entryName, elapsed_ms } = result;

  // ── Format human-readable text ───────────────────────────────────────────
  const verdictIcon = verdict === "pass" ? "✓" : verdict === "fail" ? "✗" : "⚠";
  const verdictLabel = verdict.toUpperCase();

  const lines: string[] = [
    `## Audit: ${entryId} — ${entryName}`,
    `Phase: ${phase} | Verdict: ${verdictIcon} ${verdictLabel} | ${elapsed_ms}ms`,
    `Gates: ${summary.passed}/${summary.total} passed · ${summary.failed} failed · ${summary.warned} warned`,
    "",
  ];

  // Group by status for readability
  const failed = gates.filter((g) => g.status === "fail");
  const warned = gates.filter((g) => g.status === "warn");
  const passed = gates.filter((g) => g.status === "pass");

  if (failed.length > 0) {
    lines.push("### ✗ Failed Gates (blocking)");
    for (const gate of failed) {
      lines.push(`**${gate.gate}**: ${gate.detail}`);
      if (gate.fix) lines.push(`  → Fix: ${gate.fix}`);
      if (gate.hint) lines.push(`  → Note: ${gate.hint}`);
    }
    lines.push("");
  }

  if (warned.length > 0) {
    lines.push("### ⚠ Warnings (non-blocking)");
    for (const gate of warned) {
      lines.push(`**${gate.gate}**: ${gate.detail}`);
      if (gate.fix) lines.push(`  → Fix: ${gate.fix}`);
      if (gate.hint) lines.push(`  → Note: ${gate.hint}`);
    }
    lines.push("");
  }

  if (passed.length > 0 && verdict === "pass") {
    lines.push("### ✓ All Gates Passed");
    for (const gate of passed) {
      lines.push(`  ${gate.gate}: ${gate.detail}`);
    }
  } else if (passed.length > 0) {
    lines.push(`### ✓ Passed (${passed.length}): ${passed.map((g) => g.gate).join(", ")}`);
  }

  // Next actions
  const nextActions = [];
  if (verdict !== "pass") {
    nextActions.push({
      tool: "entries",
      description: "View full entry details",
      parameters: { action: "get", entryId },
    });
    nextActions.push({
      tool: "relations",
      description: "Manage entry relations",
      parameters: { action: "list", entryId },
    });
  }

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Audit ${entryId} (${phase}): ${verdictLabel} — ${summary.passed}/${summary.total} gates passed.`,
      {
        entryId: result.entryId,
        entryName: result.entryName,
        phase: result.phase,
        clean: result.clean,
        verdict: result.verdict,
        gates: result.gates,
        summary: result.summary,
        elapsed_ms: result.elapsed_ms,
      },
      nextActions.length > 0 ? nextActions : undefined,
    ),
  };
}
